import React, {
  Dispatch, ReactElement, SetStateAction, useCallback, useEffect, useState, Ref, useRef,
} from 'react';
import {
  View, Text, StyleSheet, Pressable, FlatList, ActivityIndicator, Animated, Easing, Dimensions,
  Platform,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import Moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Colors from '../../../constants/Colors';
import { HappyScoreCategory, HappyScoreCategoryTitles } from '../../../constants/Types';
import Style from '../../../constants/Style';
import DownArrow24 from '../../../components/24pxIcons/DownArrow24';
import LocationSelectionModal from './LocationSelectionModal';
import NotificationItem from '../../../components/NotificationsLog/NotificationItem';
import getInsightNotifications from '../../../requests/Notifications/GetInsightNotifications';
import { getNotificationData, Notification } from '../../../components/NotificationsLog/data';
import PressableStyled from '../../../components/PressableStyled';
import asNumber from '../../../components/AsNumber';
import BackArrow from '../../../components/BackIcon';
import LineGraphMobile from '../../../graphs/LineGraphMobile';
import DateComponent from '../../../components/DateComponent';
import { DateRange, DateSelectionRange, updateDateRange } from '../../../redux/dateRangeSlice';
import GetMetricHappyScoreData from '../../../requests/Sensors/GetMetricHappyScoreData';
import { updateDateComparisonRange } from '../../../redux/dateComparisonRangeSlice';

interface MobileHeaderProps {
  happyScoreMetric: string,
  screen: HappyScoreCategory,
  title?: string,
  overview?: ReactElement,
  graphTitle?: string | null,
  showContent?: () => void,
  historyFooter?: ReactElement,
  graphData?,
  graphComparisonData?,
  setLocationId?: Dispatch<SetStateAction<string>>,
  setLocationCapacity?: Dispatch<SetStateAction<number>>,
  setLocationExternalId?: Dispatch<SetStateAction<string>>,
  buildingSelectionOnly?: boolean,
}

// eslint-disable-next-line no-shadow, no-unused-vars
enum TabOption { OVERVIEW = 'Overview', INSIGHTS = 'Insights' }

interface TabOptionProps {
  option: TabOption,
  live?: boolean,
}

const MobileHeader = ({
  happyScoreMetric, screen, title, overview, graphTitle, showContent, historyFooter, graphData,
  setLocationId, setLocationCapacity, setLocationExternalId, graphComparisonData,
  buildingSelectionOnly,
}: MobileHeaderProps) => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const route = useRoute();
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => state.dateRange);
  const compareRange: DateRange = useSelector((state:
    { dateComparisonRange: DateRange }) => state.dateComparisonRange);
  const { id: buildingId, name: buildingName } = useSelector((state:
    { selectedBuilding: { name: string, id: string } }) => state.selectedBuilding);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [locationSelectorVisible, setLocationSelectorVisible] = useState<boolean>(false);
  const [tabShown,
    setTabShown] = useState<TabOption>(overview ? TabOption.OVERVIEW : TabOption.INSIGHTS);
  const [insights, setInsights] = useState<Notification[] | undefined>(undefined);
  const [score, setScore] = useState<number | null>(null);
  const [timestamp, setTimestamp] = useState<string>('-');
  const [selectedInsightNotificationIds,
    setSelectedInsightNotificationIds] = useState<string[] | null>(null);
  const insightsList: Ref<FlatList | null> = useRef<FlatList | null>(null);
  const [scoreValueAnimated] = useState<Animated.Value>(new Animated.Value(0));
  const [barWidth, setBarWidth] = useState<number>(100);
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const screenHeight: number = Dimensions.get('window').height;
  const heightOffset: number = Platform.OS === 'ios' ? 345 : 360;
  const comparisonOffset: number = 45;

  const animation = Animated.timing(scoreValueAnimated, {
    duration: 500,
    useNativeDriver: false,
    toValue: (score * barWidth) / 100,
    easing: Easing.linear,
  }).start;

  useEffect(animation, [score]);

  const setRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));
  const onChange = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setRange(
      { start, end, selectedRange },
    ),
    [setRange],
  );
  const onCompareChange = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setCompareRange(
      { start, end, selectedRange },
    ),
    [setCompareRange],
  );

  useEffect(() => {
    if (!(route?.params as any)?.openInsights) return;
    setTabShown(TabOption.INSIGHTS);
    const notificationIds: string[] | undefined = (route?.params as any)?.notificationIds;
    if (notificationIds) setSelectedInsightNotificationIds(notificationIds);
    navigation.setParams({ openInsights: false, notificationIds: undefined } as any);
  }, [route]);

  useEffect(() => {
    if (!(insights?.length && selectedInsightNotificationIds?.length
      && insightsList?.current)) return;
    const index: number = insights
      ?.findIndex((item: Notification) => selectedInsightNotificationIds.includes(item.id)) ?? 0;
    const timeout = setTimeout(() => {
      insightsList?.current?.scrollToIndex({
        animated: true,
        index,
        viewPosition: 0.5,
      });
      clearTimeout(timeout);
    }, 400);
    setSelectedInsightNotificationIds(null);
  }, [insights, selectedInsightNotificationIds, insightsList]);

  useEffect(() => {
    getInsightNotifications(buildingId, screen)
      .then((res) => setInsights(getNotificationData(res?.Notifications ?? null)))
      .catch(() => setInsights(null));
    GetMetricHappyScoreData({
      metric: happyScoreMetric,
      range: { start: Moment().subtract(1, 'hour').toDate(), end: new Date() },
    }).then((data: { date: string, value: number | null }[]) => {
      if (!(data?.length > 0)) return;
      const filteredData: { date: string, value: number | null }[] = data
        .filter((point: { value: number | null }) => point.value !== null);
      let lastRecord: { date: string, value: number } | null = null;
      if (filteredData?.length) lastRecord = filteredData[filteredData.length - 1];
      if (lastRecord) setScore(asNumber(lastRecord.value));
      const timeStrings: string[] = Moment(lastRecord ? lastRecord.date : undefined)
        .calendar().split(' at');
      setTimestamp(timeStrings.join(', at'));
    });
  }, []);

  const TabButton = ({ option, live }: TabOptionProps) => {
    const selected: boolean = option === tabShown;
    return <PressableStyled
      style={[styles.tabButton, selected && { borderBottomColor: styles.tabSelected.color }]}
      onPress={() => setTabShown(option)}
      disabled={selected}
      maintainOpacityWhenDisabled
    >
      {live && <View style={styles.tabLiveCircle} />}
      <Text style={[styles.tabButtonText, selected && styles.tabSelected]}>
        {option}
      </Text>
    </PressableStyled>;
  };

  const compareEnabled: boolean = !!compareRange.start && !!compareRange.end;

  return <View style={[
    styles.container,
    Platform.OS === 'ios' ? { paddingTop: insets.top } : { marginTop: insets.top },
  ]}>
    <View style={{ flex: 1 }}>
      <Pressable
        onPress={() => setLocationSelectorVisible(true)}
        style={styles.buildingSelector}
      >
        <Text style={styles.locationName}>{locationName ?? buildingName}</Text>
        <DownArrow24 style={locationSelectorVisible ? Style.inverted : Style.upright} />
      </Pressable>
      {!graphTitle ? <>
        <View style={Style.row}>
          <Text style={styles.title}>{title ?? HappyScoreCategoryTitles[screen]}</Text>
          <Text style={[styles.title, styles.score]}>
            {score}
          </Text>
        </View>
        <View
          style={styles.progressBar}
          onLayout={({ nativeEvent: { layout: { width } } }) => setBarWidth(width)}
        >
          <Animated.View
            style={{
              width: scoreValueAnimated,
              backgroundColor: styles.score.color,
              flex: 1,
              borderRadius: styles.progressBar.borderRadius,
            }}
          >
            <LinearGradient
              style={{
                flex: 1,
                borderRadius: styles.progressBar.borderRadius,
              }}
              colors={['#5D93C5', Colors.light.hereworksDarkBlue]}
            />
          </Animated.View>
        </View>
        <View style={Style.row}>
          <Text style={styles.liveLabel}>{'Live'}</Text>
          <View style={styles.liveCircle}>
            <View style={styles.liveCircleInner} />
          </View>
          <Text style={styles.lastUpdate}>{`Last update: ${timestamp}`}</Text>
        </View>
        <View style={styles.tabSection}>
          {!!overview && <TabButton option={TabOption.OVERVIEW} />}
          <TabButton
            option={TabOption.INSIGHTS}
            live
          />
        </View>
        {tabShown === TabOption.OVERVIEW && overview}
        {tabShown === TabOption.INSIGHTS && <FlatList
          initialNumToRender={50}
          ref={insightsList}
          onScrollToIndexFailed={() => { }}
          style={styles.list}
          data={insights ?? []}
          ListEmptyComponent={<View>
            {insights === undefined && <ActivityIndicator
              style={styles.loading}
              size='large'
              animating
            />}
            {insights === null && <Text style={styles.locationName}>
              {'Error loading insights'}
            </Text>}
            {insights?.length === 0 && <Text style={styles.locationName}>
              {`No insights available for ${HappyScoreCategoryTitles[screen]}`}
            </Text>}
          </View>}
          renderItem={({ item }) => <NotificationItem
            {...item}
            buttonHidden
          />}
        />}
      </> : <View style={{ flex: 1 }}>
        <View style={Style.row}>
          <PressableStyled
            onPress={showContent}
            style={styles.backArrow}
            hitSlop={{
              top: 8, bottom: 8, left: 12, right: 12,
            }}
          >
            <BackArrow color={Colors.light.hereworksBlack600} />
          </PressableStyled>
          <Text style={styles.title}>{graphTitle}</Text>
        </View>
        <View style={styles.history}>
          <Text style={styles.historyTitle}>{'History'}</Text>
          <View style={[
            styles.datePickerContainer,
            datePickerOpen && screenHeight < 900
            && { height: screenHeight - heightOffset - (compareEnabled ? comparisonOffset : 0) },
          ]}>
            <DateComponent
              selectedRange={range.selectedRange}
              hideCalendarIcon
              {...{ onChange, onCompareChange }}
              onOpen={(open: boolean) => setDatePickerOpen(open)}
              onConfirm={() => { }}
              startDate={range.start}
              endDate={range.end}
              compareStartDate={compareRange.start}
              compareEndDate={compareRange.end}
              compareEnabled
            />
          </View>
          <View style={[styles.graphLive, compareEnabled && { marginTop: 56 }]}>
            <LineGraphMobile
              data={graphData}
              comparisonData={graphComparisonData}
            />
          </View>
          <View>
            {historyFooter}
          </View>
        </View>
      </View>}
      <LocationSelectionModal
        isVisible={locationSelectorVisible}
        hide={() => setLocationSelectorVisible(false)}
        {...{
          setLocationName,
          setLocationId,
          setLocationCapacity,
          setLocationExternalId,
          buildingSelectionOnly,
        }}
      />
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.light.hereworksBackground,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  buildingSelector: {
    ...Style.row,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksWhite500,
    backgroundColor: '#FFF',
    paddingHorizontal: 12,
    paddingVertical: 9,
    marginBottom: 12,
    height: 48,
  },
  locationName: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
    marginEnd: 8,
    flex: 1,
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    marginEnd: 8,
    flex: 1,
  },
  score: {
    flex: 0,
    color: Colors.light.hereworksDarkBlue,
  },
  progressBar: {
    marginVertical: 12,
    borderRadius: 10,
    height: 12,
    width: '100%',
    backgroundColor: Colors.light.hereworksPrimaryGrey600,
  },
  liveLabel: {
    fontSize: 11,
    lineHeight: 13,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    letterSpacing: 0.33,
    textTransform: 'uppercase',
  },
  liveCircle: {
    marginStart: 4,
    marginEnd: 12,
    width: 12,
    aspectRatio: 1,
    borderRadius: 100,
    backgroundColor: Colors.light.hereworksRed50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  liveCircleInner: {
    width: 6,
    aspectRatio: 1,
    borderRadius: 100,
    backgroundColor: Colors.light.hereworksRed500,
  },
  lastUpdate: {
    flex: 1,
    fontSize: 14,
    lineHeight: 16,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    textAlign: 'right',
  },
  tabSection: {
    marginTop: 12,
    paddingTop: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
    flexDirection: 'row',
    marginBottom: 16,
  },
  tabButton: {
    ...Style.row,
    paddingHorizontal: 12,
    paddingTop: 4,
    paddingBottom: 8,
    borderBottomWidth: 4,
    borderBottomColor: 'transparent',
  },
  tabButtonText: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
    textTransform: 'uppercase',
  },
  tabSelected: {
    color: Colors.light.hereworksBlue500,
  },
  tabLiveCircle: {
    width: 8,
    aspectRatio: 1,
    borderRadius: 100,
    marginEnd: 8,
    backgroundColor: Colors.light.hereworksRed500,
    borderWidth: 1,
    borderColor: '#FFF',
  },
  list: {
    marginHorizontal: -24,
    paddingHorizontal: 24,
  },
  loading: {
    marginTop: '40%',
  },
  backArrow: {
    marginEnd: 8,
    height: 24,
    width: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  history: {
    marginTop: 28,
    marginBottom: 24,
    backgroundColor: '#FFF',
    padding: 16,
    borderRadius: 12,
    flex: 1,
  },
  historyTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginBottom: 60,
  },
  graphDev: {
    marginStart: -12,
    marginEnd: -32,
    paddingBottom: 24,
    flex: 1,
  },
  graphLive: {
    marginStart: -12,
    marginEnd: 12,
    marginBottom: -12,
    flex: 1,
  },
  datePickerContainer: {
    zIndex: 2,
    position: 'absolute',
    width: '100%',
    top: 44,
    alignSelf: 'center',
    borderBottomWidth: 1.5,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingBottom: 16,
  },
});

export default MobileHeader;
